import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import {
  collection,
  query,
  getDocs,
  doc,
  getDoc,
  where,
  orderBy,
  limit,
  deleteDoc,
} from "firebase/firestore";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Avatar,
  Container,
  Badge,
  Tabs,
  Tab,
  Chip,
  Skeleton,
  Checkbox,
  Button,
} from "@mui/material";
import { isLoggedIn } from "../session";
import BottomNav from "./BottomToolbar";
import Atras from "./BackButton";
import { T } from "./Translate";
import { fetchBlockedIds } from "../utils/blockedUsers";
import { brandColors } from "../theme";

const ChatSkeleton = () => (
  <ListItem>
    <Box sx={{ marginRight: 2 }}>
      <Skeleton variant="circular" width={40} height={40} />
    </Box>
    <ListItemText
      primary={<Skeleton width="80%" />}
      secondary={<Skeleton width="50%" />}
    />
  </ListItem>
);

function Chats() {
  const [chats, setChats] = useState([]);
  const [blockedIds, setBlockedIds] = useState([]);
  const [authUserId, setAuthUserId] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedChats, setSelectedChats] = useState([]);
  const [deletingChats, setDeletingChats] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAuthUserId = async () => {
      const user = await isLoggedIn();
      if (user) {
        setAuthUserId(user.uid);
      } else {
        navigate("/login");
      }
    };
    fetchAuthUserId();
  }, [navigate]);

  useEffect(() => {
    if (!authUserId) {
      setBlockedIds([]);
      return;
    }
    fetchBlockedIds(authUserId).then(setBlockedIds);
  }, [authUserId]);

  useEffect(() => {
    const fetchChats = async () => {
      if (!authUserId) return;

      setLoading(true);
      try {
        const conversationsRef = collection(db, "conversations");
        const conversationsQuery = query(
          conversationsRef,
          where("participants", "array-contains", authUserId)
        );
        const conversationsSnapshot = await getDocs(conversationsQuery);

        const chatPromises = conversationsSnapshot.docs.map(
          async (conversationDoc) => {
            const conversationData = conversationDoc.data();
            const otherUserId = conversationData.participants.find(
              (id) => id !== authUserId
            );

            const messagesRef = collection(
              db,
              `conversations/${conversationDoc.id}/messages`
            );
            const lastMessageQuery = query(
              messagesRef,
              orderBy("timestamp", "desc"),
              limit(1)
            );
            const lastMessageSnapshot = await getDocs(lastMessageQuery);

            const lastMessage = lastMessageSnapshot.docs[0]?.data() || {};

            let itemData = {};
            let chatTitle = "Direct Chat";
            let chatImageUrl = "";
            
            // Determine the conversation type - prioritize itemId presence.
            // Las conversaciones de tienda (ver ProductCheckout.js) no tienen
            // itemId (usan storeId/storeName directo en el doc), así que se
            // chequean aparte para no caer en "direct" por descarte.
            let conversationType = conversationData.type || "direct";

            if (conversationData.type === "tienda") {
              conversationType = "tienda";
            } else if (conversationData.itemId) {
              conversationType = conversationData.type || "unknown";
            } else {
              // No itemId means it's a direct chat
              conversationType = "direct";
            }

            if (conversationType === "direct") {
              if (otherUserId) {
                const otherUserDocRef = doc(db, "users", otherUserId);
                const otherUserDocSnapshot = await getDoc(otherUserDocRef);
                if (otherUserDocSnapshot.exists()) {
                  const otherUserData = otherUserDocSnapshot.data();
                  chatTitle = otherUserData.displayName || "User";
                  chatImageUrl = otherUserData.profileImageUrl || "";
                }
              }
            } else if (conversationType === "tienda") {
              chatTitle = conversationData.storeName || "Tienda";
              if (conversationData.storeId) {
                const storeDocRef = doc(db, "stores", conversationData.storeId);
                const storeDocSnapshot = await getDoc(storeDocRef);
                if (storeDocSnapshot.exists()) {
                  chatImageUrl = storeDocSnapshot.data().logoUrl || "";
                }
              }
            } else if (conversationData.itemId) {
              let itemDocRef;
              switch (conversationType) {
                case "auto":
                case "car":
                  itemDocRef = doc(db, "cars", conversationData.itemId);
                  break;
                case "viaje":
                case "trip":
                  itemDocRef = doc(db, "places", conversationData.itemId);
                  break;
                default:
                  console.warn(
                    `Unknown conversation type: ${conversationType}`
                  );
              }
              if (itemDocRef) {
                const itemDocSnapshot = await getDoc(itemDocRef);
                if (itemDocSnapshot.exists()) {
                  itemData = itemDocSnapshot.data();
                  chatTitle = itemData.title || "No title";
                  chatImageUrl = itemData.images?.[0] || "";
                }
              }
            }

            let otherUserDisplayName = "Unknown user";
            if (otherUserId) {
              const otherUserDocRef = doc(db, "users", otherUserId);
              const otherUserDocSnapshot = await getDoc(otherUserDocRef);
              if (otherUserDocSnapshot.exists()) {
                otherUserDisplayName =
                  otherUserDocSnapshot.data().displayName ||
                  otherUserDisplayName;
              }
            }

            // Contar mensajes reales no leídos enviados por el otro usuario
            const unreadMessagesQuery = query(
              messagesRef,
              where("read", "==", false)
            );
            const unreadSnapshot = await getDocs(unreadMessagesQuery);
            const unreadCount = unreadSnapshot.docs.filter(
              (d) => d.data().senderId !== authUserId
            ).length;

            return {
              id: conversationDoc.id,
              type: conversationType,
              title: chatTitle,
              imageUrl: chatImageUrl,
              lastMessage: lastMessage,
              itemId: conversationData.itemId || "",
              otherUserId: otherUserId || "",
              otherUserDisplayName: otherUserDisplayName,
              unreadCount,
            };
          }
        );

        const chatResults = await Promise.all(chatPromises);
        
        // Sort by last message timestamp (most recent first)
        chatResults.sort((a, b) => {
          const timeA = a.lastMessage.timestamp?.toMillis() || 0;
          const timeB = b.lastMessage.timestamp?.toMillis() || 0;
          return timeB - timeA;
        });
        
        setChats(chatResults);
      } catch (error) {
        console.error("Error fetching chats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchChats();
  }, [authUserId]);

  useEffect(() => {
    if (!selectionMode) return;
    setSelectedChats((prev) =>
      prev.filter((id) => chats.some((chat) => chat.id === id))
    );
  }, [chats, selectionMode]);

  const handleChatClick = (chatId, type, itemId, otherUserId) => {
    if (type === "direct") {
      navigate(`/chatbox/directo/${otherUserId}`);
    } else {
      navigate(`/chatbox/${type}/${itemId}/${otherUserId}`);
    }
  };

  const handleToggleSelectionMode = () => {
    setSelectionMode((prev) => {
      if (prev) {
        setSelectedChats([]);
      }
      return !prev;
    });
  };

  const handleToggleChatSelection = (chatId) => {
    setSelectedChats((prev) =>
      prev.includes(chatId)
        ? prev.filter((id) => id !== chatId)
        : [...prev, chatId]
    );
  };

  const handleListItemClick = (chat) => {
    if (selectionMode) {
      handleToggleChatSelection(chat.id);
    } else {
      handleChatClick(chat.id, chat.type, chat.itemId, chat.otherUserId);
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedChats.length) return;
    setDeletingChats(true);
    try {
      await Promise.all(
        selectedChats.map(async (conversationId) => {
          const messagesRef = collection(
            db,
            `conversations/${conversationId}/messages`
          );
          const messagesSnapshot = await getDocs(messagesRef);
          await Promise.all(
            messagesSnapshot.docs.map((messageDoc) =>
              deleteDoc(messageDoc.ref)
            )
          );
          await deleteDoc(doc(db, "conversations", conversationId));
        })
      );
      setChats((prev) =>
        prev.filter((chat) => !selectedChats.includes(chat.id))
      );
      setSelectedChats([]);
      setSelectionMode(false);
    } catch (error) {
      console.error("Error deleting chats:", error);
    } finally {
      setDeletingChats(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const filteredChats = chats
    .filter((chat) => !blockedIds.includes(chat.otherUserId))
    .filter((chat) => {
      if (activeTab === 0) return true; // All chats
      if (activeTab === 1) return chat.type === "direct"; // Direct chats only
      if (activeTab === 2) return chat.type === "auto" || chat.type === "car"; // Car chats only
      if (activeTab === 3) return chat.type === "viaje" || chat.type === "trip"; // Trip chats only
      if (activeTab === 4) return chat.type === "tienda"; // Store chats only
      return false;
    });

  return (
    <Box sx={{ mt: { xs: 7, sm: 8 } }}>
      <AppBar position="fixed">
        <Toolbar sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", width: "100%", maxWidth: { xs: "100%", md: "980px" }, mx: "auto" }}>
            <Atras />
            <Typography variant="h6" sx={{ ml: 2 }}>
              <T>Chats</T>
            </Typography>
            <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
              {selectionMode ? (
                <>
                  <Typography variant="body2">
                    {selectedChats.length} <T>seleccionados</T>
                  </Typography>
                  <Button
                    color="error"
                    variant="contained"
                    size="small"
                    onClick={handleDeleteSelected}
                    disabled={!selectedChats.length || deletingChats}
                  >
                    {deletingChats ? <T>Eliminando...</T> : <T>Eliminar</T>}
                  </Button>
                  <Button
                    color="inherit"
                    variant="outlined"
                    size="small"
                    onClick={handleToggleSelectionMode}
                    disabled={deletingChats}
                  >
                    <T>Cancelar</T>
                  </Button>
                </>
              ) : (
                <Button color="inherit" onClick={handleToggleSelectionMode} size="small">
                  <T>Seleccionar</T>
                </Button>
              )}
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        textColor="inherit"
        TabIndicatorProps={{ style: { backgroundColor: brandColors.accent } }}
        sx={{
          maxWidth: { xs: "100%", md: "980px" },
          mx: "auto",
          "& .MuiTab-root": { color: brandColors.textSecondary },
          "& .Mui-selected": { color: brandColors.accent },
          "& .MuiTabs-scrollButtons": { color: brandColors.textSecondary },
        }}
      >
        <Tab label={<T>Todos</T>} />
        <Tab label={<T>Directos</T>} />
        <Tab label={<T>Autos</T>} />
        <Tab label={<T>Lugares</T>} />
        <Tab label={<T>Tiendas</T>} />
      </Tabs>
      <Container sx={{ mt: 1, mb: 10, maxWidth: { xs: "100%", md: "980px" } }}>
        {loading ? (
          <List>
            <ChatSkeleton />
            <Divider />
            <ChatSkeleton />
            <Divider />
            <ChatSkeleton />
            <Divider />
            <ChatSkeleton />
          </List>
        ) : filteredChats.length > 0 ? (
          <List>
            {filteredChats.map((chat) => (
              <React.Fragment key={chat.id}>
                <ListItem
                  button
                  onClick={() => handleListItemClick(chat)}
                  selected={selectionMode && selectedChats.includes(chat.id)}
                >
                  {selectionMode && (
                    <Checkbox
                      edge="start"
                      checked={selectedChats.includes(chat.id)}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleToggleChatSelection(chat.id);
                      }}
                      tabIndex={-1}
                      disableRipple
                      sx={{ mr: 1 }}
                    />
                  )}
                  <Badge
                    badgeContent={chat.unreadCount}
                    color="error"
                    invisible={chat.unreadCount === 0}
                  >
                    <Avatar src={chat.imageUrl} alt={chat.title} />
                  </Badge>
                  <ListItemText
                    sx={{ ml: 2 }}
                    primary={
                      <Box display="flex" alignItems="center">
                        <Typography variant="body1" style={{ marginRight: 8 }}>
                          {chat.title}
                        </Typography>
                        <Chip
                          label={
                            chat.type === "car"
                              ? <T>Auto</T>
                              : chat.type === "trip"
                              ? <T>Viaje</T>
                              : chat.type === "tienda"
                              ? <T>Tienda</T>
                              : <T>Directo</T>
                          }
                          color={
                            chat.type === "car"
                              ? "primary"
                              : chat.type === "trip"
                              ? "secondary"
                              : chat.type === "tienda"
                              ? "warning"
                              : "success"
                          }
                          size="small"
                        />
                      </Box>
                    }
                    secondary={
                      chat.type === "direct"
                        ? chat.lastMessage.content || <T>Sin mensajes</T>
                        : `${chat.otherUserDisplayName}: ${
                            chat.lastMessage.content || ""
                          }`
                    }
                  />
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Typography variant="body1" sx={{ mt: 5, textAlign: "center" }}>
            <T>No hay chats disponibles.</T>
          </Typography>
        )}
      </Container>
      <BottomNav />
    </Box>
  );
}

export default Chats;